import { memo } from "react"
import { css } from "@emotion/react"
import { formatDuration } from "source/shared/components/AudioPlayerControls"
import type { Episode } from "../types"
import { BORDER_COLOR } from "../utils"
import EpisodeArtworkFallback from "./EpisodeArtworkFallback"
import WaveformIcon from "./WaveformIcon"

const EpisodeItem = memo(
  function EpisodeItem({
    episode,
    onSelect,
    isSelected,
    isPlaying,
    index,
    tabIndex,
    onKeyDown,
    optionRef,
  }: {
    episode: Episode
    onSelect: (episode: Episode, index: number) => void
    isSelected: boolean
    isPlaying: boolean
    index: number
    tabIndex: 0 | -1
    onKeyDown: (event: React.KeyboardEvent, index: number) => void
    optionRef: (el: HTMLDivElement | null) => void
  }) {
    const duration = episode.duration && formatDuration(episode.duration)
    const date = episode.pubDate && new Date(episode.pubDate)
    const formattedDate =
      date && !isNaN(date.getTime()) ? date.toLocaleDateString() : ""

    const stateLabel = isPlaying
      ? " (Now playing)"
      : isSelected
        ? " (Selected)"
        : ""

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        onSelect(episode, index)
      }
      onKeyDown(e, index)
    }

    return (
      <div
        ref={optionRef}
        role="option"
        aria-selected={isSelected}
        tabIndex={tabIndex}
        onClick={() => onSelect(episode, index)}
        onKeyDown={handleKeyDown}
        aria-label={`Episode: ${episode.title} Date: ${formattedDate} Duration: ${duration} ${stateLabel}`}
        css={styles.wrapper}
      >
        {episode.artwork ? (
          <img src={episode.artwork} alt="" css={styles.artwork} />
        ) : (
          <EpisodeArtworkFallback size={48} borderRadius={4} />
        )}
        <div css={styles.content}>
          <div css={isSelected ? styles.titleSelected : styles.titleDefault}>
            {episode.title}
          </div>
          <div css={styles.metadata}>
            {[formattedDate, duration].filter(Boolean).join(" • ")}
          </div>
        </div>
        {isPlaying && <WaveformIcon />}
      </div>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.episode.audioUrl === nextProps.episode.audioUrl &&
      prevProps.episode.title === nextProps.episode.title &&
      prevProps.episode.pubDate === nextProps.episode.pubDate &&
      prevProps.episode.duration === nextProps.episode.duration &&
      prevProps.episode.artwork === nextProps.episode.artwork &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isPlaying === nextProps.isPlaying &&
      prevProps.onSelect === nextProps.onSelect &&
      prevProps.onKeyDown === nextProps.onKeyDown &&
      prevProps.tabIndex === nextProps.tabIndex &&
      prevProps.index === nextProps.index
    )
  },
)

export default EpisodeItem

const lineClamp = (lines: number) => css`
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const styles = {
  wrapper: css`
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 6px 12px;
    border-bottom: 1px solid ${BORDER_COLOR};
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover,
    &:focus-visible {
      background-color: var(--d-fill-color-neutral-080);
    }
    &:last-child {
      border-bottom: none;
    }
  `,
  artwork: css`
    width: 48px;
    height: 48px;
    border-radius: 4px;
    object-fit: cover;
  `,
  content: css`
    flex: 1;
    min-width: 0;
  `,
  titleDefault: css`
    font-size: 14px;
    color: var(--d-text-color);
    margin-bottom: 2px;
    ${lineClamp(1)}
  `,
  titleSelected: css`
    font-weight: 600;
    font-size: 14px;
    color: var(--d-text-color-interaction);
    margin-bottom: 2px;
    ${lineClamp(1)}
  `,
  metadata: css`
    font-size: 12px;
    color: var(--d-text-color-secondary);
  `,
}
